/**
 * One node's table: its columns, its rows, and a search over them.
 *
 * Used by the Objects page and by the Objects tab inside a workspace, so both
 * show the same thing and there is one place to change it.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {CopyButton, EmptyState, ErrorNotice, SkeletonRows, relativeTime} from '@/shared/components'
import { Value } from '../components'
import { TableSearch } from '../components/RowMenu'
import { ObjectRowDialog } from './ObjectRowDialog'
import { useObjectRows } from '../hooks'
import type { ObjectRow, ObjectTable } from '../types/brain.types'

export function ObjectRowsView({
  graphId,
  table,
}: {
  graphId: string
  table: ObjectTable
}) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [openRow, setOpenRow] = useState<ObjectRow | null>(null)

  const rows = useObjectRows(graphId, table.node_type_id, { q: query || undefined, page })

  return (
    <div className="brain-colstack">
      <div className="brain-panelhead">
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="row-tight">
            <h2>{table.node_type_key}</h2>
            <code className="mono small" style={{ color: 'var(--text-muted)' }}>
              {table.qualified_name}
            </code>
            <CopyButton value={table.qualified_name} label="Copy table" />
          </div>
          <div className="row-tight small faint" style={{ marginTop: 4 }}>
            <span>node_type_id</span>
            <code className="mono">{table.node_type_id}</code>
            <CopyButton value={table.node_type_id} label="Copy id" />
          </div>
        </div>
        <button
          className="btn btn-sm"
          onClick={() => navigate(`/brain/${graphId}/nodes/${table.node_type_id}`)}
        >
          Edit its columns →
        </button>
      </div>

      {!table.exists && (
        <div className="callout callout-danger">
          <p className="callout-title">This table could not be created</p>
          <p className="small mono">{table.last_error}</p>
          <p className="small">
            Records are still safe — they are in the graph either way. Fix the cause and press
            Rebuild tables.
          </p>
        </div>
      )}

      <TableSearch
        value={query}
        onChange={(next) => {
          setQuery(next)
          setPage(1)
        }}
        placeholder="Search this table…"
        count={
          rows.data
            ? `${rows.data.meta.total.toLocaleString()} row${rows.data.meta.total === 1 ? '' : 's'}`
            : undefined
        }
      />

      {rows.isLoading && <SkeletonRows rows={8} columns={6} label="Loading rows…" />}
      {rows.error && <ErrorNotice error={rows.error} onRetry={rows.refetch} />}

      {rows.data && rows.data.rows.length === 0 && (
        <EmptyState title={query ? 'Nothing matches that search' : 'This table is empty'}>
          {query
            ? 'Try a different term, or clear the box.'
            : 'Records land here as the pipeline resolves them. Attach a source on the Sources tab and ingest some rows.'}
        </EmptyState>
      )}

      {rows.data && rows.data.rows.length > 0 && (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>object_id</th>
                  <th>label</th>
                  {rows.data.table.columns.map((column) => (
                    <th
                      key={column.column_name}
                      title={`${column.property_key} · ${column.data_type}`}
                    >
                      {column.column_name}
                    </th>
                  ))}
                  <th>status</th>
                  <th>updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.data.rows.map((row) => (
                  <tr
                    key={row.object_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setOpenRow(row)}
                  >
                    <td className="mono small faint">{row.object_id.slice(0, 8)}</td>
                    <td>
                      <strong>{row.label ?? '—'}</strong>
                    </td>
                    {rows.data!.table.columns.map((column) => (
                      <td key={column.column_name} className="mono small">
                        <Value value={row[column.column_name]} />
                      </td>
                    ))}
                    <td>
                      <span
                        className={`badge ${row.status === 'ACTIVE' ? 'badge-ok' : 'badge-warn'}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="muted small">{relativeTime(row.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.data.meta.total > rows.data.meta.per_page && (
            <div className="row">
              <button
                className="btn btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="small muted">
                Page {rows.data.meta.page} of{' '}
                {Math.ceil(rows.data.meta.total / rows.data.meta.per_page)}
              </span>
              <button
                className="btn btn-sm"
                disabled={page * rows.data.meta.per_page >= rows.data.meta.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {openRow && (
        <ObjectRowDialog
          graphId={graphId}
          table={table}
          row={openRow}
          onClose={() => setOpenRow(null)}
        />
      )}
    </div>
  )
}
